import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  useCallback,
  Fragment,
} from "react";
import { toast } from "react-hot-toast";
import {
  ExclamationTriangleIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";
import { DocumentManagerV6 } from "../../Media/DocumentManagerV6";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import {
  collectChecklistGaps,
  effectiveActionsForChecklistItem,
  itemHasDocumentsGap,
  itemIsNotApplicable,
  itemRowResolved,
} from "./checklistResponseConfig";
import ChecklistGapsBar from "./ChecklistGapsBar";
import { checklistItemTitleClassName } from "./ChecklistOutcomeMark";
import { ChecklistItemCustomFields } from "./ChecklistItemCustomFields";
import { ChecklistItemDirectImageUpload } from "./ChecklistItemDirectImageUpload";
import {
  inputValuesEqual,
  preferLocalDraftValues,
  withUpdatedDocuments,
} from "./checklistInputFields";
import clsx from "clsx";

const EMPTY_ARRAY = Object.freeze([]);
/** Batch rapid outcome taps into one PUT without losing earlier items. */
const OUTCOME_DEBOUNCE_MS = 300;
/** Slightly longer for typing custom fields / descriptions. */
const TEXT_DEBOUNCE_MS = 700;

export default function ChecklistItemLinesCompactV3(props) {
  const { t } = useTranslation();
  const {
    checklist,
    items: initialItems = EMPTY_ARRAY,
    reportId,
    checklistId,
    project: projectProp,
    linkedDrawing: linkedDrawingProp,
    editable = false,
    disabled = false,
    isAdmin = false,
    validationErrors = {},
    onItemsUpdate,
    updateChecklistItems, // Function to update checklist items (should accept reportId/checklistId and items array)
    /** Parent can await flushRef.current() before close so pending saves are not dropped */
    flushRef = null,
    /** idle | pending | saving | saved | error — for dialog/page save indicators */
    onSaveUiChange = null,
    onSaveError = null,
    onEditingChange = null,
    /** "drawings" = camera + snapshot thumbs under approve/deviate (no Document Manager) */
    variant = "default",
    /** Sticky offset for the gaps bar (page = below global nav, dialog = top-0). */
    gapsStickyTopClassName,
    /**
     * Pages with ChecklistStickyFillChrome (mobile) pass showGapsBar +
     * gapsBarClassName="hidden xl:block" so desktop still gets an in-flow sticky bar.
     * Pass false when the page owns the desktop gaps bar under its checklist header.
     */
    showGapsBar = true,
    /** Optional wrapper class (e.g. hidden xl:block when chrome owns mobile gaps). */
    gapsBarClassName,
    /** Highlight matching pin on Tegninger canvas when focusing a document group */
    onFocusDocumentPin = null,
    focusedDocumentId = null,
  } = props;

  const isDrawingsLayout = variant === "drawings";
  const project = projectProp || checklist?.project || null;
  const linkedDrawing =
    linkedDrawingProp || checklist?.linked_drawing || null;
  const drawingPreviewOptions = useMemo(() => {
    if (!project?.id) return undefined;
    return {
      drawingSupport: true,
      project,
      defaultDrawing: linkedDrawing || undefined,
      lockDrawing: Boolean(linkedDrawing?.id) || isDrawingsLayout,
      autoOpenPositionPicker: Boolean(linkedDrawing?.id) || isDrawingsLayout,
      enableSketch: true,
      fullCanvasPlacement: true,
      suspendFillChecklistId: checklistId || null,
    };
  }, [project, linkedDrawing, checklistId, isDrawingsLayout]);

  const filePreviewUrl = (doc) => {
    if (!doc) return null;
    // Checklist UI: thumb / original only — never pdf_optimized (PDF-print variant)
    const url =
      doc?.preview_image_url ||
      doc?.file?.thumb?.url ||
      doc?.file?.thumb?.full_url ||
      doc?.file?.url ||
      doc?.file?.full_url ||
      doc?.url ||
      null;
    if (!url) return null;
    const name = String(doc?.title || doc?.file?.filename || "").toLowerCase();
    if (name.endsWith(".pdf")) return null;
    return url;
  };

  // "Open in new tab" target: the original, not the ~200x200 thumb filePreviewUrl() prefers.
  const fileFullUrl = (doc) => {
    if (!doc) return null;
    const url =
      doc?.file?.full_url ||
      doc?.file?.url ||
      doc?.preview_image_url ||
      doc?.url ||
      null;
    if (!url) return null;
    const name = String(doc?.title || doc?.file?.filename || "").toLowerCase();
    if (name.endsWith(".pdf")) return null;
    return url;
  };

  /** Group primary docs with their drawing snapshot; exclude nested snapshot docs from the flat list. */
  const buildDocumentPinGroups = (documents) => {
    // Drop null/undefined slots (same guard as DrawingDocumentPinGroups).
    const docs = (Array.isArray(documents) ? documents : []).filter(
      (d) => d != null && typeof d === "object"
    );
    const snapshotIds = new Set(
      docs
        .map(
          (d) =>
            d.drawing_snapshot_document_id || d.drawing_snapshot_document?.id
        )
        .filter(Boolean)
        .map(String)
    );
    return docs
      .filter((d) => d.id != null && !snapshotIds.has(String(d.id)))
      .map((doc) => {
        const snapshot =
          doc.drawing_snapshot_document ||
          docs.find(
            (d) =>
              d.id != null &&
              String(d.id) === String(doc.drawing_snapshot_document_id)
          ) ||
          null;
        const hasPin =
          doc.drawing_position_x != null && doc.drawing_position_y != null;
        return { doc, snapshot, hasPin };
      });
  };

  const [items, setItems] = useState(initialItems);
  const [savingItemIds, setSavingItemIds] = useState(() => new Set());
  const [saveUi, setSaveUi] = useState("idle");
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [selectedItemForDialog, setSelectedItemForDialog] = useState(null);
  const [tempDescription, setTempDescription] = useState("");
  const [isDeviationDialog, setIsDeviationDialog] = useState(false); // Track if dialog was opened from isDeviation change
  useLayoutEffect(() => {
    onEditingChange?.(descriptionDialogOpen);
    return () => onEditingChange?.(false);
  }, [descriptionDialogOpen, onEditingChange]);
  const [inputFieldErrorIds, setInputFieldErrorIds] = useState(() => new Set());
  const dialogJustOpenedRef = useRef(false); // Track if dialog just opened to prevent immediate close
  const textareaRef = useRef(null); // Ref for textarea to focus it when dialog opens
  /** Pre-deviation item snapshot so cancel can restore UI + re-queue dropped autosaves */
  const deviationRevertItemRef = useRef(null);

  // Use ref to track if we're updating from parent to prevent infinite loop
  const isUpdatingFromParent = useRef(false);
  const prevItemsRef = useRef(initialItems);
  // Items currently being edited locally — don't clobber with stale parent props
  const dirtyItemIdsRef = useRef(new Set());
  const onItemsUpdateRef = useRef(onItemsUpdate);
  const updateChecklistItemsRef = useRef(updateChecklistItems);
  const onSaveUiChangeRef = useRef(onSaveUiChange);
  const onSaveErrorRef = useRef(onSaveError);
  onSaveErrorRef.current = onSaveError;
  /** Latest full payload per item id waiting to be persisted */
  const pendingByIdRef = useRef(new Map());
  const inFlightRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const savedUiResetRef = useRef(null);
  const flushWaitersRef = useRef([]);
  const reportIdRef = useRef(reportId);
  const checklistIdRef = useRef(checklistId);

  useEffect(() => {
    onItemsUpdateRef.current = onItemsUpdate;
  }, [onItemsUpdate]);

  useEffect(() => {
    updateChecklistItemsRef.current = updateChecklistItems;
  }, [updateChecklistItems]);

  useEffect(() => {
    onSaveUiChangeRef.current = onSaveUiChange;
  }, [onSaveUiChange]);

  useEffect(() => {
    reportIdRef.current = reportId;
    checklistIdRef.current = checklistId;
  }, [reportId, checklistId]);

  const publishSaveUi = useCallback((next) => {
    // Cancel a pending saved→idle reset whenever a newer status is published,
    // so a late timer cannot overwrite saving/pending/error.
    if (next !== "idle" && savedUiResetRef.current) {
      window.clearTimeout(savedUiResetRef.current);
      savedUiResetRef.current = null;
    }
    setSaveUi(next);
    onSaveUiChangeRef.current?.(next);
  }, []);

  const resolveFlushWaiters = useCallback((ok = true) => {
    const waiters = flushWaitersRef.current;
    flushWaitersRef.current = [];
    waiters.forEach((resolve) => resolve(ok));
  }, []);

  // Stable props for DocumentManager — new inline arrays every render would refetch docs for every item
  const filterModuleParams = useMemo(
    () => (reportId ? [reportId] : EMPTY_ARRAY),
    [reportId]
  );
  const checklistItemIdsKey = items
    .map((i) => i?.id ?? "")
    .filter(Boolean)
    .join("|");
  const checklistItemIds = useMemo(() => {
    if (!checklistItemIdsKey) return EMPTY_ARRAY;
    return checklistItemIdsKey.split("|");
    // Only recompute when the id set changes — not on every input_values keystroke
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklistItemIdsKey]);

  // Update items when initialItems change, but only if it's actually different
  useEffect(() => {
    // Compare by JSON string to avoid unnecessary updates
    const prevItemsStr = JSON.stringify(prevItemsRef.current);
    const newItemsStr = JSON.stringify(initialItems);

    if (newItemsStr !== prevItemsStr && !isUpdatingFromParent.current) {
      if (initialItems && Array.isArray(initialItems)) {
        // Keep in-progress local edits (e.g. custom fields) if parent still has stale values
        const merged =
          dirtyItemIdsRef.current.size === 0
            ? initialItems
            : initialItems.map((apiItem) => {
                if (!apiItem?.id || !dirtyItemIdsRef.current.has(apiItem.id)) {
                  return apiItem;
                }
                const local = prevItemsRef.current.find((i) => i.id === apiItem.id);
                return local || apiItem;
              });
        setItems(merged);
        prevItemsRef.current = merged;
      }
    }
  }, [initialItems]);

  // Preserve scroll position when items update (matches checklistItemLinesCompact.js).
  useEffect(() => {
    const scrollPosition = window.scrollY;
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollPosition);
    });
  }, [items]);

  // Focus textarea when dialog opens
  useEffect(() => {
    if (descriptionDialogOpen && textareaRef.current) {
      // Use requestAnimationFrame and setTimeout to ensure DOM is ready and dialog is fully rendered
      let timeoutId;
      const frameId = requestAnimationFrame(() => {
        timeoutId = setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
            // Move cursor to end of text
            const length = textareaRef.current.value.length;
            textareaRef.current.setSelectionRange(length, length);
            // Ensure textarea is not disabled
            textareaRef.current.disabled = false;
            textareaRef.current.readOnly = false;
          }
        }, 200);
      });

      return () => {
        cancelAnimationFrame(frameId);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
  }, [descriptionDialogOpen]);



  const resolveChecklistFromUpdateResponse = (response) => {
    if (response?.items != null) return response;
    if (response?.data?.items != null) return response.data;
    return null;
  };

  const buildItemPayload = (item) => ({
    id: item.id,
    title: item.title,
    description: item.description,
    isApproved: item.isApproved,
    isDeviation: item.isDeviation,
    is_not_applicable: item.is_not_applicable,
    response_code: item.response_code,
    deviation_id: item.isDeviation ? item.deviation_id : null,
    row_order: item.row_order,
    documents_required: item.documents_required,
    input_values: item.input_values || {},
  });

  const syncItemsFromSaveResponse = (checklistPayload, savedItems) => {
    const savedList = Array.isArray(savedItems) ? savedItems : [savedItems];
    if (!savedList.length || !checklistPayload?.items) return;

    const savedById = new Map(
      savedList.filter((item) => item?.id != null).map((item) => [item.id, item])
    );
    if (savedById.size === 0) return;

    isUpdatingFromParent.current = true;
    // Merge before scheduling React state updates. Notifying the parent from
    // inside a state updater runs during render and can replay stale snapshots.
    const updatedItems = prevItemsRef.current.map((prevItem) => {
      const savedItem = savedById.get(prevItem.id);
      if (!savedItem) return prevItem;

      const updatedItemFromApi = checklistPayload.items.find(
        (i) => i.id === savedItem.id
      );
      if (!updatedItemFromApi) return prevItem;

      // Keep local draft when it differs from the (possibly trimmed) server echo
      const localValues = prevItem.input_values || {};
      const apiValues = updatedItemFromApi.input_values || {};
      const mergedValues = preferLocalDraftValues(localValues, apiValues);
      const localMatchesApi = inputValuesEqual(localValues, apiValues);
      const localDescription = prevItem.description ?? "";
      const apiDescription = updatedItemFromApi.description ?? "";
      const descriptionMatchesApi = localDescription === apiDescription;
      const hasNewerPending = pendingByIdRef.current.has(savedItem.id);

      if (localMatchesApi && descriptionMatchesApi && !hasNewerPending) {
        dirtyItemIdsRef.current.delete(savedItem.id);
      }

      if (hasNewerPending) {
        // Keep local draft; trailing queued save owns the next persist
        return prevItem;
      }

      return {
        ...updatedItemFromApi,
        input_values: mergedValues,
        description: descriptionMatchesApi
          ? updatedItemFromApi.description
          : prevItem.description,
        documents: updatedItemFromApi.documents ?? prevItem.documents,
      };
    });

    prevItemsRef.current = updatedItems;
    setItems(updatedItems);
    onItemsUpdateRef.current?.(updatedItems);

    setTimeout(() => {
      isUpdatingFromParent.current = false;
    }, 0);
  };

  const requeueUnlessSuperseded = useCallback((snapshot) => {
    snapshot.forEach((payload, id) => {
      if (!pendingByIdRef.current.has(id)) {
        pendingByIdRef.current.set(id, payload);
      }
    });
  }, []);

  /**
   * Persist all queued item changes in one PUT.
   * While a request is in flight, newer edits stay in pendingByIdRef and flush
   * again in finally — so rapid check-offs never overwrite each other with
   * minimal `{ id, row_order }` stubs.
   */
  const persistPending = useCallback(async () => {
    const updateFn = updateChecklistItemsRef.current;
    const targetId = reportIdRef.current || checklistIdRef.current;
    if (!updateFn || !targetId) {
      pendingByIdRef.current.clear();
      resolveFlushWaiters();
      return;
    }

    if (pendingByIdRef.current.size === 0) {
      if (!inFlightRef.current) {
        resolveFlushWaiters();
      }
      return;
    }

    if (inFlightRef.current) {
      return;
    }

    const pendingSnapshot = new Map(pendingByIdRef.current);
    pendingByIdRef.current.clear();
    inFlightRef.current = true;
    setSavingItemIds(new Set(pendingSnapshot.keys()));
    publishSaveUi("saving");

    let succeeded = false;
    try {
      const currentItems = prevItemsRef.current || [];
      const itemsToSend = currentItems.map((prevItem) => {
        const pending = pendingSnapshot.get(prevItem.id);
        if (pending) return pending;
        return {
          id: prevItem.id,
          row_order: prevItem.row_order,
        };
      });

      // Include any queued ids missing from local list (should be rare)
      pendingSnapshot.forEach((payload, id) => {
        if (!itemsToSend.some((row) => row.id === id)) {
          itemsToSend.push(payload);
        }
      });

      const response = await updateFn(targetId, {
        checklist: {
          items: itemsToSend,
        },
      });

      const isTransportError =
        response?.isAxiosError ||
        response instanceof Error ||
        (response &&
          typeof response === "object" &&
          response.response &&
          !response.items &&
          !response.data?.items &&
          !response.errors);

      if (isTransportError) {
        const errorMessage =
          response?.response?.data?.errors?.join?.(", ") ||
          response?.response?.data?.error ||
          response?.message ||
          t("quality.checklist.item.saveFailed");
        if (onSaveErrorRef.current) onSaveErrorRef.current({ ...(response || {}), errors: [errorMessage] });
        else toast.error(errorMessage);
        requeueUnlessSuperseded(pendingSnapshot);
        publishSaveUi("error");
        return;
      }

      const checklistPayload = resolveChecklistFromUpdateResponse(response);
      if (checklistPayload) {
        succeeded = true;
        syncItemsFromSaveResponse(
          checklistPayload,
          Array.from(pendingSnapshot.values())
        );
        if (pendingByIdRef.current.size === 0) {
          publishSaveUi("saved");
          if (savedUiResetRef.current) {
            window.clearTimeout(savedUiResetRef.current);
          }
          savedUiResetRef.current = window.setTimeout(() => {
            publishSaveUi("idle");
          }, 1500);
        }
      } else if (response?.errors) {
        const errorMessage = Array.isArray(response.errors)
          ? response.errors.join(", ")
          : String(response.errors);
        if (onSaveErrorRef.current) onSaveErrorRef.current({ ...(response || {}), errors: [errorMessage] });
        else toast.error(errorMessage);
        requeueUnlessSuperseded(pendingSnapshot);
        publishSaveUi("error");
      } else {
        // Unknown shape — treat as failure so changes are not silently dropped
        if (onSaveErrorRef.current) onSaveErrorRef.current({ errors: [t("quality.checklist.item.saveFailed")] });
        else toast.error(t("quality.checklist.item.saveFailed"));
        requeueUnlessSuperseded(pendingSnapshot);
        publishSaveUi("error");
      }
    } catch (error) {

      const errorMessage =
        error?.response?.data?.errors?.join(", ") ||
        error?.response?.data?.error ||
        t("quality.checklist.item.saveFailed");
      if (onSaveErrorRef.current) onSaveErrorRef.current(error);
      else toast.error(errorMessage);
      requeueUnlessSuperseded(pendingSnapshot);
      publishSaveUi("error");
    } finally {
      inFlightRef.current = false;
      setSavingItemIds(new Set());
      // Only chain another request when this one succeeded and newer edits
      // arrived during flight — never auto-retry a failed persist in a loop.
      if (succeeded && pendingByIdRef.current.size > 0) {
        void persistPending();
      } else {
        // Resolve false on failure so closeWithFlush can keep the dialog open.
        resolveFlushWaiters(succeeded);
      }
    }
  }, [publishSaveUi, requeueUnlessSuperseded, resolveFlushWaiters, t]);

  const schedulePersist = useCallback(
    (delayMs) => {
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      if (delayMs <= 0) {
        void persistPending();
        return;
      }

      if (!inFlightRef.current) {
        publishSaveUi("pending");
      }

      debounceTimerRef.current = window.setTimeout(() => {
        debounceTimerRef.current = null;
        void persistPending();
      }, delayMs);
    },
    [persistPending, publishSaveUi]
  );

  /** Queue latest item payload; coalesces per id and batches across items. */
  const queueItemSave = useCallback(
    (item, { debounceMs = TEXT_DEBOUNCE_MS } = {}) => {
      if (!item?.id) return;
      pendingByIdRef.current.set(item.id, buildItemPayload(item));
      dirtyItemIdsRef.current.add(item.id);
      schedulePersist(debounceMs);
    },
    [schedulePersist]
  );

  const cancelQueuedItem = useCallback(
    (itemId) => {
      if (itemId == null) return;
      pendingByIdRef.current.delete(itemId);
      if (
        pendingByIdRef.current.size === 0 &&
        !inFlightRef.current &&
        debounceTimerRef.current
      ) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
        publishSaveUi("idle");
      }
    },
    [publishSaveUi]
  );

  const flushPendingSaves = useCallback(async () => {
    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (pendingByIdRef.current.size === 0 && !inFlightRef.current) {
      return;
    }

    return new Promise((resolve) => {
      flushWaitersRef.current.push(resolve);
      if (!inFlightRef.current) {
        void persistPending();
      }
    });
  }, [persistPending]);

  useLayoutEffect(() => {
    if (!flushRef) return undefined;
    flushRef.current = flushPendingSaves;
    return () => {
      flushRef.current = null;
    };
  }, [flushRef, flushPendingSaves]);

  useEffect(() => {
    onSaveUiChangeRef.current?.(saveUi);
  }, [saveUi]);

  // Flush pending saves on unmount (best-effort). Prefer parent closeWithFlush.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (savedUiResetRef.current) {
        window.clearTimeout(savedUiResetRef.current);
      }
      if (pendingByIdRef.current.size > 0 && !inFlightRef.current) {
        void persistPending();
      }
    };
  }, [persistPending]);

  const patchItemResponse = (item, key) => {
    const clear = item.response_code === key;
    if (clear) {
      return {
        ...item,
        response_code: null,
        isApproved: false,
        isDeviation: false,
        is_not_applicable: false,
      };
    }
    if (key === "performed" || key === "approved") {
      return {
        ...item,
        response_code: key,
        isApproved: true,
        isDeviation: false,
        is_not_applicable: false,
      };
    }
    if (key === "na") {
      return {
        ...item,
        response_code: "na",
        isApproved: false,
        isDeviation: false,
        is_not_applicable: true,
      };
    }
    return {
      ...item,
      response_code: "deviation",
      isApproved: false,
      isDeviation: true,
      is_not_applicable: false,
    };
  };

  const isActionSelected = (item, key) => {
    if (item.response_code === key) return true;
    if (!item.response_code) {
      if (
        key === "approved" &&
        item.isApproved &&
        !item.isDeviation &&
        !item.is_not_applicable
      )
        return true;
      if (key === "deviation" && item.isDeviation) return true;
      if (key === "na" && item.is_not_applicable) return true;
    }
    return false;
  };

  // Semantic styling for selected outcome buttons (V3 design)
  const outcomeSelectedClass = (key) => {
    if (key === "deviation") return "border-red-600 bg-red-600 text-white shadow-sm";
    if (key === "na") return "border-slate-500 bg-slate-500 text-white shadow-sm";
    return "border-emerald-600 bg-emerald-600 text-white shadow-sm"; // approved / performed
  };

  // Left accent reflecting the item's resolved state (V3 design)
  const itemAccentClass = (item) => {
    if (item?.isApproved) return "bg-emerald-500";
    if (item?.isDeviation) return "bg-red-500";
    if (item?.is_not_applicable) return "bg-slate-400";
    return "bg-gray-200";
  };

  const handleInputValuesChange = (item, nextValues) => {
    const updatedItem = {
      ...item,
      input_values: nextValues,
    };

    if (item.id) {
      dirtyItemIdsRef.current.add(item.id);
    }

    setItems((prevItems) => {
      const updatedItems = prevItems.map((prevItem) =>
        prevItem.id === item.id ? updatedItem : prevItem
      );
      prevItemsRef.current = updatedItems;
      return updatedItems;
    });

    // Do not call onItemsUpdate here — parent updates remount/refetch DocumentManagers
    // for every keystroke. Parent is synced after a successful save instead.

    if (item.id) {
      setInputFieldErrorIds((prev) => {
        if (!prev.has(item.id)) return prev;
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      queueItemSave(updatedItem, { debounceMs: TEXT_DEBOUNCE_MS });
    }
  };

  // Handle deviation change
  const onDeviationChange = (e, item) => {
    const isDeviationChecked = e.target.checked;

    // If deviation is checked, open the note dialog and DON'T save yet
    if (isDeviationChecked) {

      // Hold this line out of the queue until description is confirmed
      cancelQueuedItem(item.id);
      // Keep pre-deviation snapshot for cancel (restore outcome + re-queue dirty edits)
      deviationRevertItemRef.current = item;
      const updatedItem = {
        ...item,
        isDeviation: true,
        isApproved: false,
        is_not_applicable: false,
        response_code: "deviation",
      };

      // Update local state to show isDeviation as checked
      setItems((prevItems) => {
        const next = prevItems.map((prevItem) =>
          prevItem.id === item.id ? updatedItem : prevItem
        );
        prevItemsRef.current = next;
        return next;
      });

      // Open description dialog immediately - don't save until description is saved
      setSelectedItemForDialog(updatedItem);
      setTempDescription(updatedItem.description || "");
      setIsDeviationDialog(true); // Mark that this dialog was opened from isDeviation
      dialogJustOpenedRef.current = true;
      setDescriptionDialogOpen(true);
      // Reset flag after a short delay to allow dialog to fully render
      setTimeout(() => {
        dialogJustOpenedRef.current = false;
      }, 100);
    } else {
      // If unchecking deviation, queue a fast save
      const updatedItem = {
        ...item,
        isDeviation: false,
        isApproved: false,
        is_not_applicable: false,
        response_code: null,
      };

      setItems((prevItems) => {
        const next = prevItems.map((prevItem) =>
          prevItem.id === item.id ? updatedItem : prevItem
        );
        prevItemsRef.current = next;
        return next;
      });

      if (item.id) {
        queueItemSave(updatedItem, { debounceMs: OUTCOME_DEBOUNCE_MS });
      }
    }
  };

  const onOutcomeSelect = (item, key) => {

    if (key === "deviation") {
      if (item.isDeviation) {
        onDeviationChange({ target: { checked: false } }, item);
      } else {
        onDeviationChange({ target: { checked: true } }, item);
      }
      return;
    }
    const updatedItem = patchItemResponse(item, key);
    setItems((prevItems) => {
      const next = prevItems.map((prevItem) =>
        prevItem.id === item.id ? updatedItem : prevItem
      );
      prevItemsRef.current = next;
      return next;
    });
    if (item.id) {
      queueItemSave(updatedItem, { debounceMs: OUTCOME_DEBOUNCE_MS });
    }
  };

  // Handle document change (upload, delete, etc.)
  const handleDocumentsChange = (newDocuments, item) => {
    let updatedItem = null;
    setItems((prevItems) => {
      const current = prevItems.find((prevItem) => prevItem.id === item.id) || item;
      updatedItem = withUpdatedDocuments(current, newDocuments);
      const next = prevItems.map((prevItem) =>
        prevItem.id === item.id ? updatedItem : prevItem
      );
      prevItemsRef.current = next;
      return next;
    });

    if (updatedItem?.id) {
      queueItemSave(updatedItem, { debounceMs: OUTCOME_DEBOUNCE_MS });
    }
  };

  useEffect(() => {
    const onDocumentsUploaded = (event) => {
      const { module, moduleParam, documents } = event.detail || {};
      if (module !== "checklist_item" || !moduleParam || !documents?.length) {
        return;
      }

      setItems((prevItems) => {
        const item = prevItems.find((i) => String(i.id) === String(moduleParam));
        if (!item) {
          return prevItems;
        }

        const merged = [...(item.documents || [])].filter((d) => d?.id != null);
        documents.forEach((doc) => {
          if (doc?.id == null) return;
          if (!merged.some((existing) => String(existing.id) === String(doc.id))) {
            merged.push(doc);
          }
        });

        const updatedItem = { ...item, documents: merged };
        prevItemsRef.current = prevItems.map((prevItem) =>
          prevItem.id === item.id ? updatedItem : prevItem
        );
        if (item.id) {
          queueItemSave(updatedItem, { debounceMs: OUTCOME_DEBOUNCE_MS });
        }

        return prevItemsRef.current;
      });
    };

    window.addEventListener("promang:documents-uploaded", onDocumentsUploaded);
    return () =>
      window.removeEventListener("promang:documents-uploaded", onDocumentsUploaded);
  }, [queueItemSave]);

  // Handle description save
  const handleDescriptionSave = () => {
    if (selectedItemForDialog) {
      // Validate that description is not empty if this is a deviation dialog
      if (isDeviationDialog && (!tempDescription || tempDescription.trim() === "")) {
        toast.error("Beskrivelse er påkrevd ved avvik");
        return;
      }

      const updatedItem = {
        ...selectedItemForDialog,
        description: tempDescription,
      };

      setItems((prevItems) => {
        const next = prevItems.map((prevItem) =>
          prevItem.id === selectedItemForDialog.id ? updatedItem : prevItem
        );
        prevItemsRef.current = next;
        return next;
      });

      // Now save the item with both isDeviation and description
      if (selectedItemForDialog.id) {
        queueItemSave(updatedItem, { debounceMs: OUTCOME_DEBOUNCE_MS });
      }
    }
    deviationRevertItemRef.current = null;
    setDescriptionDialogOpen(false);
    setSelectedItemForDialog(null);
    setTempDescription("");
    setIsDeviationDialog(false);
  };

  const openDescriptionDialog = (item) => {
    setSelectedItemForDialog(item);
    setTempDescription(item.description || "");
    setIsDeviationDialog(false); // Not opened from isDeviation
    dialogJustOpenedRef.current = true;
    setDescriptionDialogOpen(true);
    // Reset flag after a short delay to allow dialog to fully render
    setTimeout(() => {
      dialogJustOpenedRef.current = false;
    }, 100);
  };

  // Handle description dialog cancel
  const handleDescriptionCancel = () => {
    if (isDeviationDialog && selectedItemForDialog) {
      // Restore pre-deviation snapshot (clears response_code / outcome flags)
      const revertedItem = deviationRevertItemRef.current
        ? { ...deviationRevertItemRef.current }
        : {
            ...selectedItemForDialog,
            isDeviation: false,
            isApproved: false,
            is_not_applicable: false,
            response_code: null,
          };
      deviationRevertItemRef.current = null;

      setItems((prevItems) => {
        const next = prevItems.map((prevItem) =>
          prevItem.id === selectedItemForDialog.id ? revertedItem : prevItem
        );
        prevItemsRef.current = next;
        return next;
      });

      // Re-queue edits that cancelQueuedItem dropped when opening the note dialog
      if (
        selectedItemForDialog.id &&
        dirtyItemIdsRef.current.has(selectedItemForDialog.id)
      ) {
        queueItemSave(revertedItem, { debounceMs: OUTCOME_DEBOUNCE_MS });
      }
    }
    setDescriptionDialogOpen(false);
    setSelectedItemForDialog(null);
    setTempDescription("");
    setIsDeviationDialog(false);
  };

  const canUploadImage = (item) => {
    if (!editable || disabled) {
      return false;
    }
    return true;
  };

  // All users can view documents, even when checklist is approved/completed (read-only)
  const canViewDocuments = () => {
    return true;
  };

  const getItemValidationErrors = (item) => {
    const itemIndex = items.findIndex((i) => i.id === item.id);
    if (itemIndex === -1) return null;

    return (
      validationErrors[`items.${itemIndex}`] ||
      validationErrors[`items.${item.id}`] ||
      null
    );
  };

  if (!items || items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-12 text-center text-sm text-gray-500">
        Ingen sjekkliste-punkter tilgjengelig
      </div>
    );
  }

  const gapSummary = collectChecklistGaps(items);
  const resolvedCount = gapSummary.resolvedCount;
  const deviationCount = gapSummary.deviationCount;
  const progressPct = items.length ? (resolvedCount / items.length) * 100 : 0;
  const gapsStickyClass = gapsStickyTopClassName
    || (isDrawingsLayout
      ? "top-0"
      : "top-[var(--dashboard-topbar-height,4rem)]");
  const guidanceTitle = t("quality.checklist.item.guidanceTitle");

  return (
    <div className="space-y-4 sm:space-y-5">
      {showGapsBar ? (
        <div className={gapsBarClassName}>
          <ChecklistGapsBar items={items} stickyTopClassName={gapsStickyClass} />
        </div>
      ) : null}
      {/* Progress summary */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
        <span className="font-semibold text-emerald-700">
          {items.length} punkter
        </span>
        <span className="text-gray-400">·</span>
        <span className="text-gray-500">
          {resolvedCount} av {items.length} besvart
        </span>
        {deviationCount > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-200">
            <ExclamationTriangleIcon className="h-3.5 w-3.5" />
            {deviationCount} avvik
          </span>
        )}
        {(saveUi === "pending" || saveUi === "saving") && (
          <span
            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600"
            role="status"
            aria-live="polite"
          >
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            {saveUi === "saving"
              ? t("quality.checklist.item.saving")
              : t("quality.checklist.item.savePending")}
          </span>
        )}
        {saveUi === "saved" && (
          <span
            className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700"
            role="status"
            aria-live="polite"
          >
            <CheckIcon className="h-3.5 w-3.5" />
            {t("quality.checklist.item.saved")}
          </span>
        )}
        {saveUi === "error" && (
          <span
            className="inline-flex items-center gap-1 text-xs font-medium text-red-600"
            role="status"
            aria-live="polite"
          >
            <ExclamationTriangleIcon className="h-3.5 w-3.5" />
            {t("quality.checklist.item.saveError")}
          </span>
        )}
        <div className="ml-auto h-1.5 w-28 overflow-hidden rounded-full bg-gray-100 sm:w-44">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all duration-300"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {items.map((item, index) => {
        const itemErrors = getItemValidationErrors(item);
        const isSaving = savingItemIds.has(item.id);
        const isResolved = itemRowResolved(item);
        const isNa = itemIsNotApplicable(item);
        const guidanceText = String(item?.guidance || "").trim();

        const itemDocIds = isDrawingsLayout
          ? buildDocumentPinGroups(item?.documents)
              .map(({ doc }) => doc?.id)
              .filter(Boolean)
          : [];
        const itemOwnsFocusedDoc =
          focusedDocumentId != null &&
          itemDocIds.some((id) => String(id) === String(focusedDocumentId));

        return (
          <div
            key={item.id || index}
            data-checklist-item-id={item.id || undefined}
            data-drawing-focused-doc={
              itemOwnsFocusedDoc ? String(focusedDocumentId) : undefined
            }
            className={clsx(
              "relative overflow-hidden rounded-2xl border bg-white p-4 transition-shadow sm:p-5",
              itemErrors
                ? "border-red-200 ring-1 ring-red-100"
                : isSaving
                ? "border-blue-200 ring-1 ring-blue-100"
                : "border-gray-200 shadow-sm hover:shadow-md",
              isNa && "bg-slate-50/80"
            )}
          >
            {/* Status accent bar — only when resolved */}
            {isResolved && (
              <div
                className={clsx(
                  "absolute inset-y-0 left-0 w-1.5",
                  itemAccentClass(item)
                )}
                aria-hidden
              />
            )}

            <div className="flex items-start gap-3 sm:gap-4">
              {/* Status badge */}
              <div
                className={clsx(
                  "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-sm font-semibold",
                  item?.isApproved
                    ? "bg-emerald-500 text-white"
                    : item?.isDeviation
                    ? "bg-red-500 text-white"
                    : isNa
                    ? "bg-slate-400 text-white"
                    : "border border-gray-200 bg-gray-50 text-gray-500"
                )}
              >
                {item?.isApproved ? (
                  <CheckIcon className="h-5 w-5" />
                ) : item?.isDeviation ? (
                  <ExclamationTriangleIcon className="h-5 w-5" />
                ) : (
                  index + 1
                )}
              </div>

              {/* Content column (indented under title, matching design) */}
              <div className="min-w-0 flex-1 space-y-3">
                {/* Title + actions */}
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                  <h4
                    className={checklistItemTitleClassName(
                      item,
                      "w-full min-w-0 hyphens-none break-words pt-1 text-[15px] font-bold leading-snug text-pretty text-gray-900 sm:text-base"
                    )}
                  >
                    {item.title || `Punkt ${index + 1}`}
                  </h4>

                  <div className="flex w-full flex-shrink-0 flex-wrap items-center gap-1.5 sm:w-auto sm:gap-2">
                    {!isDrawingsLayout && (
                      <>
                        {/* Document Manager (standard checklist views only) */}
                        <div
                          className={clsx(
                            "rounded-lg border px-1.5 py-1 sm:px-1 sm:py-0.5",
                            itemHasDocumentsGap(item)
                              ? "border-red-300 bg-red-50"
                              : item?.documents?.length > 0
                              ? "border-emerald-300 bg-emerald-50"
                              : "border-gray-200"
                          )}
                        >
                          <DocumentManagerV6
                            subtitle={`Sjekklistepunkt: ${item.title}`}
                            module="checklist_item"
                            moduleParam={item?.id}
                            documents={item?.documents}
                            mode="mini"
                            maxDisplay={4}
                            showUpload={canUploadImage(item)}
                            showExisting={canViewDocuments(item)}
                            allowDelete={canUploadImage(item)}
                            allowEdit={canUploadImage(item)}
                            allowCopy={canUploadImage(item) && !!reportId}
                            allowMove={canUploadImage(item) && !!reportId}
                            autoLoad={false}
                            disabled={!canViewDocuments(item) || isSaving}
                            onDocumentsChange={(documents) => handleDocumentsChange(documents, item)}
                            filterModule={reportId ? "report" : null}
                            filterModuleParams={filterModuleParams}
                            checklistId={checklistId}
                            checklistItemIds={checklistItemIds}
                            project={project}
                            defaultDrawing={linkedDrawing}
                            lockDrawing={Boolean(linkedDrawing?.id)}
                            autoOpenPositionPicker={Boolean(linkedDrawing?.id)}
                            enableSketch
                          />
                        </div>

                        {canUploadImage(item) && item?.id && (
                          <ChecklistItemDirectImageUpload
                            item={item}
                            disabled={isSaving}
                            previewOptions={drawingPreviewOptions}
                            className="[&>span:not(.sr-only)]:hidden sm:[&>span:not(.sr-only)]:inline"
                          />
                        )}
                      </>
                    )}

                    {/* Description / edit button */}
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => openDescriptionDialog(item)}
                        className="rounded-lg p-2 text-gray-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-600 touch-manipulation sm:p-1.5"
                        title="Rediger beskrivelse"
                      >
                        <PencilIcon className="h-5 w-5 sm:h-4 sm:w-4" />
                      </button>
                    )}

                    {/* Saving indicator */}
                    {isSaving && (
                      <div
                        className="flex items-center whitespace-nowrap text-xs text-blue-600"
                        role="status"
                        aria-live="polite"
                      >
                        <div className="mr-1 h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600" />
                        <span className="hidden sm:inline">
                          {t("quality.checklist.item.saving")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Guidance (VEILEDNING) */}
                {guidanceText && (
                  <div className="rounded-lg bg-emerald-50/70 px-3 py-2.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 sm:text-[11px]">
                      {guidanceTitle}
                    </p>
                    <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-700 sm:text-sm">
                      {guidanceText}
                    </p>
                  </div>
                )}

                {/* Deviation / extra description */}
                {item.description && (
                  <div className="break-words rounded-lg bg-gray-50 px-3 py-2 text-xs italic text-gray-600 sm:text-sm">
                    {item.description}
                  </div>
                )}

                {/* Outcome segmented control */}
                {!disabled ? (
                  <div className="flex gap-1 rounded-xl bg-gray-50 p-1">
                    {effectiveActionsForChecklistItem(
                      item,
                      checklist?.checklist_type
                    ).map((key) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => onOutcomeSelect(item, key)}
                        className={clsx(
                          "flex-1 rounded-lg px-2 py-2.5 text-center text-xs font-semibold transition-colors touch-manipulation sm:text-sm",
                          isActionSelected(item, key)
                            ? outcomeSelectedClass(key)
                            : "text-gray-500 hover:bg-white hover:text-gray-700"
                        )}
                      >
                        {t(`quality.checklist.actions.${key}`)}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    {item?.isApproved && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800">
                        <CheckIcon className="h-3.5 w-3.5" /> Godkjent
                      </span>
                    )}
                    {item?.isDeviation && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-100 px-3 py-1 text-xs font-medium text-red-800">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" /> Avvik
                      </span>
                    )}
                    {item?.is_not_applicable && (
                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-medium text-slate-800">
                        Ikke aktuelt
                      </span>
                    )}
                    {!item?.isApproved &&
                      !item?.isDeviation &&
                      !item?.is_not_applicable && (
                        <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                          Ikke sjekket
                        </span>
                      )}
                  </div>
                )}

                {/* Custom input fields (MÅLINGER / DATA) */}
                <ChecklistItemCustomFields item={item}
                  fields={item.input_fields}
                  values={item.input_values || {}}
                  disabled={disabled || !editable}
                  showErrors={Boolean(item.id && inputFieldErrorIds.has(item.id))}
                  onChange={(nextValues) =>
                    handleInputValuesChange(item, nextValues)
                  }
                />

                {/* Drawings layout: pin groups (snapshot + photo) under approve/deviate */}
                {isDrawingsLayout && (
                  <div className="rounded-xl border border-[#e5e8ec] bg-[#f8fafc] p-2.5 sm:p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#59616d]">
                        {t("drawings.viewer.checklist_item_docs")}
                      </p>
                      {canUploadImage(item) && item?.id && (
                        <ChecklistItemDirectImageUpload
                          item={item}
                          disabled={isSaving}
                          previewOptions={drawingPreviewOptions}
                          className="!min-h-[2.25rem]"
                          onDocumentsChange={(docs) => {
                            if (!Array.isArray(docs) || !docs.length) return;
                            const merged = [
                              ...(item.documents || []),
                              ...docs.filter(
                                (d) =>
                                  !(item.documents || []).some(
                                    (existing) => String(existing.id) === String(d.id)
                                  )
                              ),
                            ];
                            handleDocumentsChange(merged, item);
                          }}
                        />
                      )}
                    </div>
                    {(() => {
                      const groups = buildDocumentPinGroups(item?.documents);
                      if (!groups.length) {
                        return (
                          <p className="text-[12px] text-[#59616d]">
                            {t("drawings.viewer.checklist_item_docs_empty")}
                          </p>
                        );
                      }
                      return (
                        <ul className="space-y-2">
                          {groups.map(({ doc, snapshot, hasPin }, groupIndex) => {
                            const photoUrl = filePreviewUrl(doc);
                            const snapshotUrl = filePreviewUrl(snapshot);
                            const photoFullUrl = fileFullUrl(doc) || photoUrl;
                            const snapshotFullUrl =
                              fileFullUrl(snapshot) || snapshotUrl;
                            const isFocused =
                              focusedDocumentId != null &&
                              String(focusedDocumentId) === String(doc.id);
                            return (
                              <li key={doc.id}>
                                <button
                                  type="button"
                                  data-drawing-doc-id={doc.id}
                                  onClick={() =>
                                    onFocusDocumentPin?.(doc, { pinFocus: true })
                                  }
                                  onMouseEnter={() =>
                                    onFocusDocumentPin?.(doc)
                                  }
                                  onFocus={() =>
                                    onFocusDocumentPin?.(doc)
                                  }
                                  className={clsx(
                                    "w-full rounded-xl border bg-white p-2 text-left transition-shadow touch-manipulation",
                                    isFocused
                                      ? "border-[#2b6ef0] shadow-[0_0_0_2px_rgba(43,110,240,0.25)]"
                                      : "border-[#e5e8ec] hover:border-[#bfdcf7] hover:shadow-sm"
                                  )}
                                >
                                  <div className="mb-1.5 flex items-center justify-between gap-2">
                                    <span className="text-[11px] font-semibold text-[#14171c]">
                                      {t("drawings.viewer.checklist_pin_group", {
                                        n: groupIndex + 1,
                                      })}
                                    </span>
                                    {hasPin ? (
                                      <span className="rounded-full bg-[#eaf8ef] px-2 py-0.5 text-[10px] font-semibold text-[#0b7a3b]">
                                        {t("drawings.viewer.checklist_item_pinned")}
                                      </span>
                                    ) : (
                                      <span className="rounded-full bg-[#f1f5f9] px-2 py-0.5 text-[10px] font-semibold text-[#64748b]">
                                        {t("drawings.viewer.checklist_unpinned_docs")}
                                      </span>
                                    )}
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className="overflow-hidden rounded-lg border border-[#e5e8ec] bg-[#f8fafc]">
                                      <p className="border-b border-[#e5e8ec] px-1.5 py-1 text-[9px] font-semibold uppercase tracking-wide text-[#59616d]">
                                        {t("drawings.observation.snapshot_title")}
                                      </p>
                                      {snapshotUrl ? (
                                        <a
                                          href={snapshotFullUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          className="block"
                                        >
                                          <img
                                            src={snapshotUrl}
                                            alt=""
                                            className="h-20 w-full object-cover sm:h-24"
                                          />
                                        </a>
                                      ) : (
                                        <div className="flex h-20 items-center justify-center px-1 text-center text-[10px] text-[#94a3b8] sm:h-24">
                                          —
                                        </div>
                                      )}
                                    </div>
                                    <div className="overflow-hidden rounded-lg border border-[#e5e8ec] bg-[#f8fafc]">
                                      <p className="border-b border-[#e5e8ec] px-1.5 py-1 text-[9px] font-semibold uppercase tracking-wide text-[#59616d]">
                                        {t("drawings.observation.field.photo")}
                                      </p>
                                      {photoUrl ? (
                                        <a
                                          href={photoFullUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          className="block"
                                        >
                                          <img
                                            src={photoUrl}
                                            alt={doc.title || ""}
                                            className="h-20 w-full object-cover sm:h-24"
                                          />
                                        </a>
                                      ) : (
                                        <div className="flex h-20 items-center justify-center px-1 text-center text-[10px] text-[#94a3b8] sm:h-24">
                                          {doc.title ||
                                            t("drawings.viewer.checklist_item_doc")}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {hasPin ? (
                                    <p className="mt-1.5 text-[10px] font-medium text-[#2b6ef0]">
                                      {t("drawings.viewer.show_on_drawing")}
                                    </p>
                                  ) : null}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      );
                    })()}
                  </div>
                )}

                {/* Validation Errors */}
                {itemErrors && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-2">
                    <div className="flex items-start">
                      <ExclamationTriangleIcon className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-red-400" />
                      <div className="text-xs text-red-700">
                        {itemErrors.description && <div>{itemErrors.description}</div>}
                        {itemErrors.documents && <div>{itemErrors.documents}</div>}
                        {itemErrors.input_fields && <div>{itemErrors.input_fields}</div>}
                      </div>
                    </div>
                  </div>
                )}

                {/* Document requirement warning */}
                {itemHasDocumentsGap(item) && (
                  <div className="text-xs italic text-red-500">
                    ⚠️ Dokumenter er påkrevd for dette punktet
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Description Dialog */}
      <Transition.Root show={descriptionDialogOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative"
          style={{ zIndex: 2100 }}
          initialFocus={textareaRef}
          onClose={handleDescriptionCancel}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="pm-vv-fill bg-black/50 transition-opacity" />
          </Transition.Child>

          <div className="pm-vv-fill z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-0 text-center sm:items-center sm:p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full transform overflow-hidden rounded-t-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:rounded-lg sm:px-6">
                  <div>
                    <Dialog.Title as="h3" className="mb-3 text-base font-medium leading-6 text-gray-900 sm:mb-4 sm:text-lg">
                      {isDeviationDialog ? "Legg til beskrivelse for avvik" : "Rediger beskrivelse"}
                    </Dialog.Title>
                    {isDeviationDialog && (
                      <div className="mb-3 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700 sm:text-sm">
                        Beskrivelse er påkrevd ved avvik
                      </div>
                    )}
                    <div className="mt-2">
                      <textarea
                        ref={textareaRef}
                        value={tempDescription}
                        disabled={false}
                        readOnly={false}
                        onChange={(e) => {
                          setTempDescription(e.target.value);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Ensure textarea is focused and enabled when clicked
                          if (textareaRef.current) {
                            textareaRef.current.focus();
                            textareaRef.current.disabled = false;
                            textareaRef.current.readOnly = false;
                          }
                        }}
                        onFocus={(e) => {
                          e.stopPropagation();
                          // Ensure textarea is enabled when focused
                          if (textareaRef.current) {
                            textareaRef.current.disabled = false;
                            textareaRef.current.readOnly = false;
                          }
                        }}
                        onKeyDown={(e) => {
                          // Allow Escape to close dialog (Dialog component handles this automatically)
                          // Don't stop propagation for other keys to allow normal typing
                        }}
                        className={clsx(
                          "min-h-[120px] w-full resize-y rounded-md border p-3 text-base sm:min-h-[150px] sm:p-2 sm:text-sm",
                          isDeviationDialog && (!tempDescription || tempDescription.trim() === "")
                            ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                        )}
                        placeholder={isDeviationDialog ? "Beskriv avviket her..." : "Skriv beskrivelse her..."}
                      />
                    </div>
                  </div>
                  <div className="mt-5 gap-2 sm:mt-4 sm:flex sm:flex-row-reverse sm:gap-0">
                    <button
                      type="button"
                      onClick={handleDescriptionSave}
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-blue-500 active:bg-blue-700 touch-manipulation sm:ml-3 sm:w-auto sm:py-2 sm:text-sm"
                    >
                      Lagre
                    </button>
                    <button
                      type="button"
                      onClick={handleDescriptionCancel}
                      className="inline-flex w-full justify-center rounded-md bg-white px-4 py-3 text-base font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 active:bg-gray-100 touch-manipulation sm:mt-0 sm:w-auto sm:py-2 sm:text-sm"
                    >
                      Avbryt
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

    </div>
  );
}
